import { CommonModule,formatDate } from '@angular/common';
import { ChangeDetectionStrategy,Component,Inject,Input,LOCALE_ID,OnDestroy,OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialogConfig, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Actions,ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment,{ Moment } from 'moment/moment';
import { BehaviorSubject,combineLatest,firstValueFrom,merge,Observable,Subscription,switchMap } from 'rxjs';
import { debounceTime,distinctUntilChanged,filter,map,shareReplay,skip,tap } from 'rxjs/operators';
import { ContractParticipantEntity } from '../../../../dave-data-module/entities/mdqx/contract-participant.entity';
import { CustomerResolver } from '../../../../dave-data-module/guards/customer.resolver';
import { ContractGroupTypeResolver } from '../../../../dave-data-module/guards/mdqx/contract-group-type.resolver';
import { FrontendDate } from '../../../../dave-data-module/helper/backend-frontend-conversion.helper';
import { getFetched$ } from '../../../../dave-data-module/helper/helper';
import { MutationCreateContractParticipantArgs } from '../../../../dave-data-module/mdqx-graphql-types';
import { State } from '../../../../dave-data-module/State';
import { BaseActionTypes } from '../../../../dave-data-module/State/actions/base.actions';
import {
ContractParticipantActionTypes,
DeleteContractParticipantRequest,
PostContractParticipantRequest
} from '../../../../dave-data-module/State/actions/mdqx/contract-participant.actions';
import {
getCustomersFetched
} from '../../../../dave-data-module/State/selectors/customers.selectors';
import {
getContractGroupType,
getContractGroupTypeFetched,
getContractParticipants,
getContractParticipantsFetched,getContracts,getContractsFetched
} from '../../../../dave-data-module/State/selectors/mdqx/selectors';
import { DaveLoadingModule } from '../../../../dave-loading/dave-loading.module';
import {
ISelectedElement,ITableConfig,
TableContentType
} from '../../../../dave-select-user/components/multi-select-table/multi-select-table.component';
import {
SelectUserDialogBase
} from '../../../../dave-select-user/components/select-user-dialogs/select-user-dialog-base';
import { DaveSelectUserModule } from '../../../../dave-select-user/dave-select-user.module';
import { AppButtonModule } from '../../../../dave-utils-module/app-button-module/app-button.module';
import { AppFilterModule } from '../../../../dave-utils-module/app-filter-module/app-filter.module';
import {
FilterOption,FILTER_TYPE_MULTI_SELECT
} from '../../../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import { isNotNullOrUndefined,SearchQueriesDebounceTime,stringSearch } from '../../../../helper/helper';
import { CustomLabelService } from '../../../../services/custom-label.service';
import { DefaultFilterService,FilterApps } from '../../../../services/default-filter.service';
import { LoadingService } from '../../../../services/loading.service';
import {
    DetailListDialogReturn,
    DetailListTemplateDialogComponent,
    DetailListTemplateDialogData,
} from '../../../../detail-list-template-dialog/components/detail-list-template-dialog.component';

interface ITableData {
    contractId: number;
    selectedForm: FormControl<boolean>;
    contractGroupTypeId: number;
    name: TableContentType<{ id: string }> & { __typename: 'string' };
    entryDate: TableContentType<{ id: string }> & { __typename: 'date' };
    endDate: TableContentType<{ id: string }> & { __typename: 'date' };
    ausnahmedatum: TableContentType<{ id: string }> & { __typename: 'date' };
}
interface SelectecContractType extends ISelectedElement {
    contractParticipantId: number;
    contractId: number;
}
enum filterNames {
    contractGroupType = 'contractGroupTypeId',
}
interface IFilter {
    [filterNames.contractGroupType]: string[];
}
export interface SelectContractComponentPreFilter {
    contractGroupTypeId: number[];
}
export interface SelectContractsForCustomerDialogData {
    customerId: number;
}
@Component({
    selector: 'app-select-contracts-for-customer-dialog',
    standalone: true,
    imports: [CommonModule, AppButtonModule, AppFilterModule, DaveLoadingModule, DaveSelectUserModule, FontAwesomeModule, MatDialogModule, MatTooltipModule, MatBadgeModule],
    templateUrl: './select-contracts-for-customer-dialog.component.html',
    styleUrls: ['./select-contracts-for-customer-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectContractsForCustomerDialogComponent implements OnInit, OnDestroy {
    public static readonly DefaultConfig: MatDialogConfig = SelectUserDialogBase.DefaultConfig;
    public FilterSettings$: Observable<FilterOption[]> = this.store.select(getContractGroupTypeFetched).pipe(
        filter((f) => f),
        switchMap(() => this.store.select(getContractGroupType)),
        map((customerTypes) => {
            const ctValues = {};
            customerTypes.forEach((c) => {
                ctValues[c.Id.toString()] = c.Name;
            });
            return [
                {
                    Name: filterNames.contractGroupType,
                    Type: FILTER_TYPE_MULTI_SELECT,
                    Label: 'Vertragsgruppe',
                    Values: ctValues,
                },
            ];
        }),
    );
    public FilterValues$: BehaviorSubject<IFilter> = new BehaviorSubject({
        [filterNames.contractGroupType]: [],
    });
    private preFilter$ = new BehaviorSubject<SelectContractComponentPreFilter | null>(null);
    @Input() set preFilter(value: SelectContractComponentPreFilter) {
        this.preFilter$.next(value);
    }
    get preFilter(): SelectContractComponentPreFilter {
        return this.preFilter$.value;
    }
    public FilterAmount$ = this.FilterValues$.pipe(map((v) => (v[filterNames.contractGroupType]?.length ? 1 : 0)));
    public SearchString: BehaviorSubject<string> = new BehaviorSubject<string>('');

    selectedContracts$: Observable<SelectecContractType[] | null>;

    TableDataConfig: ITableConfig[] = [
        {
            header: 'Name',
            id: 'name',
            sortAccessor: (cell: TableContentType<any> & { __typename: 'string' }) => cell.content || '',
        },
        {
            header: 'Beitrittsdatum',
            id: 'entryDate',
            width: '9.5rem',
        },
        {
            header: 'Teilnahmeende',
            id: 'endDate',
            width: '9.5rem',
        },
        {
            header: 'Ausnahmedatum',
            id: 'ausnahmedatum',
            width: '9.5rem',
        },
    ];
    FilteredTableData$: Observable<ITableData[]>;
    TableData$: Observable<ITableData[]>;

    private subscriptions: Subscription[] = [];
    public ContentResolved$: Observable<boolean>;
    public dirty = false;
    public customerId: number;
    private contractParticipantsByCustomer$: Observable<ContractParticipantEntity[]>;
    constructor(
        private store: Store<State>,
        public LS: LoadingService,
        private defaultFilter: DefaultFilterService,
        customerRes: CustomerResolver,
        contractGroupTypeRes: ContractGroupTypeResolver,
        protected cls: CustomLabelService,
        @Inject(MAT_DIALOG_DATA) data: SelectContractsForCustomerDialogData,
        @Inject(LOCALE_ID) locale: string,
        private actions$: Actions,
        private dialogRef: MatDialogRef<SelectContractsForCustomerDialogComponent>,
        private dialog: MatDialog,
    ) {
        this.customerId = data.customerId;
        this.ContentResolved$ = combineLatest([
            this.store.select(getCustomersFetched).pipe(
                distinctUntilChanged(),
                tap((fetched) => {
                    if (!fetched) {
                        customerRes.resolve();
                    }
                }),
            ),
            this.store.select(getContractGroupTypeFetched).pipe(
                distinctUntilChanged(),
                tap((fetched) => {
                    if (!fetched) {
                        contractGroupTypeRes.resolve();
                    }
                }),
            ),
        ]).pipe(
            map((values) => values.every((v) => v)),
            distinctUntilChanged(),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.contractParticipantsByCustomer$ = getFetched$(this.store, getContractParticipantsFetched, getContractParticipants).pipe(
            map((cps) => cps.filter((c) => c.CustomerId === this.customerId)),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.selectedContracts$ = this.contractParticipantsByCustomer$.pipe(
            switchMap((cps) =>
                getFetched$(this.store, getContractsFetched, getContracts).pipe(
                    map((contracts) => {
                        return cps.map<SelectecContractType>((contractParticipant) => ({
                            contractParticipantId: contractParticipant.Id,
                            contractId: contractParticipant.ContractId,
                            label: `${
                                contracts?.find(c => c.Id === contractParticipant.ContractId)?.Name
                            }${
                                contractParticipant.EntryDate && contractParticipant.EndDate
                                    ? ` (${formatDate(contractParticipant.EntryDate, 'shortDate', locale)} - ${formatDate(contractParticipant.EndDate, 'shortDate', locale)})`
                                    : contractParticipant.EntryDate
                                        ? ` Beitrittsdatum: ${formatDate(contractParticipant.EntryDate, 'shortDate', locale)}`
                                        : contractParticipant.EndDate ? ` Teilnahmeende: ${formatDate(contractParticipant.EndDate, 'shortDate', locale)}` : ''
                            }${
                                contractParticipant.Ausnahmedatum
                                    ? ` Ausnahmedatum: ${formatDate(contractParticipant.Ausnahmedatum, 'shortDate', locale)}`
                                    : ''
                            }`
                        }));
                    }),
                ),
            ),
        );
        // this.store.select(getCustomerDictionary).pipe(map((customers) => (this.Dialogdata.selectedCustomers || []).map((id) => ({ label: customers[id]?.DisplayName, customerId: id }))));
        this.TableData$ = combineLatest([
            this.contractParticipantsByCustomer$,
            this.store.select(getContractsFetched).pipe(
                filter((v) => !!v),
                switchMap(() => this.store.select(getContracts)),
                map((contracts) => contracts.filter((c) => !c.Deleted)),
            ),
            this.preFilter$,
        ]).pipe(
            map(([contractParticipants, contracts, preFilter]) => {
                const filteredContracts = preFilter && preFilter.contractGroupTypeId?.length ? contracts.filter((contract) => contract.ContractGroupTypeId && preFilter.contractGroupTypeId.includes(contract.ContractGroupTypeId)) : contracts;
                return filteredContracts.map<ITableData>((contract) => {
                    const cp: ContractParticipantEntity = contractParticipants.find((cp) => cp.ContractId === contract.Id);
                    // const type = c.CustomerTypeIds && types.find((ct) => ct.Id === c.CommissionTypeId);
                    return {
                        contractId: contract.Id,
                        contractGroupTypeId: contract.ContractGroupTypeId,
                        selectedForm: new FormControl<boolean>(!!cp),
                        name: {
                            __typename: 'string',
                            content: contract.Name,
                        },
                        entryDate: {
                            __typename: 'date',
                            formControl: new FormControl<Moment>(cp ? (cp.EntryDate ? moment(cp?.EntryDate) : null) : moment()),
                        },
                        endDate: {
                            __typename: 'date',
                            formControl: new FormControl<Moment>(cp ? (cp.EndDate ? moment(cp?.EndDate) : null) : null),
                        },
                        ausnahmedatum: {
                            __typename: 'date',
                            formControl: new FormControl<Moment>(cp ? (cp.Ausnahmedatum ? moment(cp?.Ausnahmedatum) : null) : null),
                        },
                    };
                });
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.FilteredTableData$ = this.TableData$.pipe(
            switchMap((rows) =>
                this.FilterValues$.pipe(
                    map((filter) => (filter[filterNames.contractGroupType]?.length ? rows.filter((row) => row.contractGroupTypeId && filter[filterNames.contractGroupType].includes(row.contractGroupTypeId.toString())) : rows)),
                ),
            ),
            switchMap((rows) => {
                return this.SearchString.pipe(
                    debounceTime(SearchQueriesDebounceTime),
                    map((searchString) => (!searchString && rows) || rows.filter((r) => [r.name.content].filter(isNotNullOrUndefined).some((s) => stringSearch(s, searchString)))),
                );
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
    }
    ngOnInit(): void {
        firstValueFrom(this.defaultFilter.GetFilterByApp$(FilterApps.MDQXSelectCustomerForContractPopup)).then((filterValues) => {
            let temp = {
                [filterNames.contractGroupType]: isNotNullOrUndefined(filterValues[filterNames.contractGroupType]) ? filterValues[filterNames.contractGroupType] : [],
            };
            this.FilterValues$.next(temp);
        });

        this.subscriptions.push(
            this.FilterValues$.pipe(skip(2)).subscribe((val) => {
                if (Object.keys(val).length !== 0) {
                    this.defaultFilter.SetFilterByApp(FilterApps.MDQXSelectCustomerForContractPopup, val);
                }
            }),
            this.TableData$.pipe(
                // tap(() => this.dirty = false),
                switchMap((td) => merge(...td.map((t) => t.selectedForm.valueChanges.pipe(map(() => t.selectedForm.dirty))))),
            ).subscribe((v) => {
                if (v) {
                    this.dirty = true;
                }
            }),
            this.TableData$.pipe(
                switchMap((data) =>
                    this.selectedContracts$.pipe(
                        tap((selectedContracts) =>
                            data.forEach((d) => {
                                const isSelected = selectedContracts.some((u) => u.contractId === d.contractId);

                                if (isSelected && (d.selectedForm.enabled || !d.selectedForm.value)) {
                                    d.selectedForm.setValue(true, { emitEvent: false });
                                    d.selectedForm.disable();
                                    d.entryDate.formControl.disable();
                                    d.endDate.formControl.disable();
                                    d.ausnahmedatum.formControl.disable();
                                } else if (!isSelected && (d.selectedForm.disabled || d.selectedForm.value)) {
                                    d.selectedForm.setValue(false, { emitEvent: false });
                                    d.selectedForm.enable();
                                    d.entryDate.formControl.enable();
                                    d.endDate.formControl.enable();
                                    d.ausnahmedatum.formControl.enable();
                                }
                            }),
                        ),
                    ),
                ),
            ).subscribe(),
        );
    }
    RemoveSelected(entity: SelectecContractType) {
        this.store.dispatch(
            new DeleteContractParticipantRequest({
                id: entity.contractParticipantId,
            }),
        );
    }
    async clickSelected(entity: SelectecContractType) {
        // alert('not implemented')
        const cps = await firstValueFrom(this.contractParticipantsByCustomer$);
        const cp = cps.find(c => c.ContractId === entity.contractId);
        if (cp) {
            this.dialog
                .open<DetailListTemplateDialogComponent, DetailListTemplateDialogData, DetailListDialogReturn>(DetailListTemplateDialogComponent, {
                    ...DetailListTemplateDialogComponent.DefaultConfig,
                    data: {
                        Data: {
                            Properties: [
                                {
                                    key: 'Beitrittsdatum',
                                    formControl: new FormControl<Moment>(moment(cp.EntryDate)),
                                    options: {
                                        specialInput: {
                                            date: true,
                                        },
                                    },
                                },
                                {
                                    key: 'Teilnahmeende',
                                    formControl: new FormControl<Moment>(moment(cp.EndDate)),
                                    options: {
                                        specialInput: {
                                            date: true,
                                        },
                                    },
                                },
                                {
                                    key: 'Ausnahmedatum',
                                    formControl: new FormControl<Moment>(moment(cp.Ausnahmedatum)),
                                    options: {
                                        specialInput: {
                                            date: true,
                                        },
                                    },
                                },
                            ],
                        },
                        Editing: true,
                    },
                })
                .afterClosed()
                .subscribe((ret) => {
                    if (ret?.Action === 'save') {
                        this.store.dispatch(
                            new PostContractParticipantRequest({
                                contractId: entity.contractId,
                                customerId: this.customerId,
                                entryDate: (() => {
                                    const val = ret.DialogData.Data.Properties[0].formControl.value;
                                    if (!val || !val.isValid()) return null;
                                    const date = val.toDate ? val.toDate() : val; // falls already Date
                                    return FrontendDate(date);
                                })(),
                                endDate: (() => {
                                    const val = ret.DialogData.Data.Properties[1].formControl.value;
                                    if (!val || !val.isValid()) return null;
                                    const date = val.toDate ? val.toDate() : val;
                                    return FrontendDate(date);
                                })(),
                                ausnahmedatum: (() => {
                                    const val = ret.DialogData.Data.Properties[2].formControl.value;
                                    if (!val || !val.isValid()) return null;
                                    const date = val.toDate ? val.toDate() : val;
                                    return FrontendDate(date);
                                })(),
                            }),
                        );
                    } else if (ret?.Action === 'delete') {
                        this.RemoveSelected(entity);
                    }
                });
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    // public DisplayCustomers$: Observable<DisplayCustomer[]>;
    // protected selectedCustomers$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
    //
    // constructor(
    //     public DialogRef: MatDialogRef<SelectCustomersForContractDialogComponent>,
    //     private store: Store<State>,
    //     @Inject(MAT_DIALOG_DATA) public Data: ContractEntity,
    //     private dialog: MatDialog,
    // ) {}
    //
    // ngOnInit(): void {
    //     this.DisplayCustomers$ = combineLatest([
    //         this.store
    //             .select(getContractParticipants)
    //             .pipe(map(cps => cps.filter(cp => cp.ContractId === this.Data.Id))),
    //         this.selectedCustomers$,
    //         this.store.select(getCustomers).pipe(map(cu => cu.filter(c => !c.Deleted))),
    //     ]).pipe(
    //         tap(([contractParticipants, selected, customers]) => {
    //             if (!contractParticipants || customers.length < 1) {
    //                 // es gibt keine Kunden
    //                 this.dialog
    //                     .open<InfoDialogComponent, InfoDialogData>(InfoDialogComponent, {
    //                         data: {
    //                             heading: 'Keine Kunden Vorhanden',
    //                             paragraph:
    //                                 'Es sind keine Kunden vorhanden die ausgewählt werden könnten. Bitte legen sie zuerst welche an. Dafür gehen Sie in die Kundenverwaltung und legen einen neuen Kunden an.',
    //                             routingButtonRoute: '/' + CustomerAdministrationMeta.Path + '/neuerKunde',
    //                             routingButtonText: 'Hier geht es zur Mitarbeiterverwaltung',
    //                         },
    //                         maxWidth: '50rem',
    //                     })
    //                     .afterClosed()
    //                     .pipe(
    //                         take(1),
    //                         tap(res => {
    //                             this.DialogRef.close();
    //                         }),
    //                     )
    //                     .subscribe();
    //             }
    //         }),
    //         map(([contractParticipants, selected, customers]) =>
    //             customers.map(c => {
    //                 return {
    //                     Selected: selected.includes(c.Id),
    //                     Customer: c,
    //                 };
    //             }),
    //         ),
    //     );
    //     this.store
    //         .select(getContractParticipants)
    //         .pipe(
    //             take(1),
    //             map(cps =>
    //                 cps
    //                     .filter(cp => cp.ContractId === this.Data.Id)
    //                     .map(cp => cp.CustomerId),
    //             ),
    //             // remove duplicates
    //             map(ids => ids.filter((value, index) => ids.indexOf(value) === index)),
    //         )
    //         .subscribe(ids => this.selectedCustomers$.next(ids));
    // }
    //
    // public ToggleSelected(id: number) {
    //     if (this.selectedCustomers$.getValue().includes(id)) {
    //         this.selectedCustomers$.next(this.selectedCustomers$.getValue().filter(i => i !== id));
    //     } else {
    //         const value = Object.assign([], this.selectedCustomers$.getValue());
    //         value.push(id);
    //         this.selectedCustomers$.next(value);
    //     }
    // }
    //
    // public SelectAll() {
    //     this.store
    //         .select(getCustomers)
    //         .pipe(
    //             take(1),
    //             map(cs => cs.map(c => c.Id)),
    //         )
    //         .subscribe(ids => this.selectedCustomers$.next(ids));
    // }
    //
    // public Close() {
    //     this.DialogRef.close();
    // }
    //
    public Save() {
        // this.store
        //     .select(getContractParticipants)
        //     .pipe(
        //         take(1),
        //         map(cps => {
        //             for (const customerId of this.selectedCustomers$.getValue()) {
        //                 if (!cps.find(cp => cp.CustomerId === customerId && cp.ContractId === this.Data.Id)) {
        //                     this.store.dispatch(
        //                         new PostContractParticipantRequest({
        //                             customerId: customerId,
        //                             contractId: this.Data.Id,
        //                         }),
        //                     );
        //                 }
        //             }
        //             cps.filter(cp => cp.ContractId === this.Data.Id).map(cp => {
        //                 if (!isNotNullOrUndefined(this.selectedCustomers$.getValue().find(cId => cId === cp.CustomerId))) {
        //                     this.store.dispatch(
        //                         new DeleteContractParticipantRequest({
        //                             id: cp.Id,
        //                         }),
        //                     );
        //                 }
        //             });
        //         }),
        //     )
        //     .subscribe();
        // this.DialogRef.close();
    }
    async Submit() {
        this.LS.startLoading('app-select-customers-for-contract-dialog_submit');
        const tableData = await firstValueFrom(this.TableData$);
        const alreadySelected = await firstValueFrom(this.selectedContracts$);
        const toAdd: Array<MutationCreateContractParticipantArgs> = [];
        tableData.forEach((d) => {
            if (d.selectedForm.value && !alreadySelected.some((s) => s.contractId === d.contractId)) {
                toAdd.push({
                    customerId: this.customerId,
                    contractId: d.contractId,
                    entryDate: d.entryDate.formControl.value ? FrontendDate(d.entryDate.formControl.value.toDate()) : null,
                    endDate: d.endDate.formControl.value ? FrontendDate(d.endDate.formControl.value.toDate()) : null,
                    ausnahmedatum: d.ausnahmedatum.formControl.value ? FrontendDate(d.ausnahmedatum.formControl.value.toDate()) : null,
                });
            }
        });
        let error = false;
        if (toAdd.length > 0) {
            toAdd.forEach((Payload) => {
                this.store.dispatch(new PostContractParticipantRequest(Payload));
            });
            await firstValueFrom(
                this.actions$.pipe(
                    ofType(BaseActionTypes.ErrorAction, ContractParticipantActionTypes.PostResponse),
                    tap((action) => {
                        if (action.type === BaseActionTypes.ErrorAction.type) {
                            error = true;
                        }
                    }),
                    skip(toAdd.length - 1),
                ),
            );
        }
        this.LS.endLoading('app-select-customers-for-contract-dialog_submit');
    }
}
